import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
	Row, Col, FormGroup, Checkbox, ControlLabel,
} from 'react-bootstrap';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import utils, { constants } from '@utils';
import { Select } from '@components/common';
import { thunks } from '@redux/jobs';

class Filters extends Component {
	/*
		Since the required API calls are made based on query string/redux state of options selected,
		query string and selected options sometimes do not align when navigating from a different page to job list.
		This lifecycle method makes an API call to get the most up-to-date options based on redux values
	*/
	componentDidMount = () => {
		const { applyCriteria, filter : { deleted, includeInactive } } = this.props;
		applyCriteria(
			[
				{
					property : 'deleted',
					value    : deleted,
				},
				{
					property : 'includeInactive',
					value    : includeInactive,
				},
			],
		);
	}

	/* If we change the type of jobs we are looking at on the job list,
	we should reset the paging value to 1 so that we do not try to show a second page when it doesn't exist */
	componentDidUpdate = (prevProps) => {
		const { applyCriteria, activePage } = this.props;
		if (prevProps.activePage !== activePage) {
			applyCriteria(
				[
					{
						property : 'pageNumber',
						value    : 1,
					},
				],
			);
		}
	}

	shouldComponentUpdate = ({
		activePage,
		hideDateRange,
		userTypeId,
		locations,
		loading,
		filter,
	}) => {
		const lastProps = this.props;

		return (
			!isEqual(activePage, lastProps.activePage)
			|| !isEqual(hideDateRange, lastProps.hideDateRange)
			|| !isEqual(userTypeId, lastProps.userTypeId)
			|| !isEqual(locations, lastProps.locations)
			|| !isEqual(loading, lastProps.loading)
			|| !isEqual(filter, lastProps.filter)
		);
	}

	generateFilterSelector = ({
		label,
		value,
		options,
		loading,
		property,
	}) => (
		<FormGroup>
			<ControlLabel>{ label }</ControlLabel>
			<Select
				value={value}
				name={property}
				onChange={this.handleOnFilter(property, 'select')}
				options={options}
				isLoading={loading}
				isClearable
			/>
		</FormGroup>
	)

	handleOnFilter = (property, fieldType) => (event) => {
		const { applyCriteria } = this.props;
		let value = event;

		switch (fieldType) {
			case 'select':
				value = get(event, ['value'], null);
				break;

			case 'checkbox':
				if (property === 'prePay') {
					// A value of 'false' causes prepay jobs to be hidden entirely.
					// We want to either show prepay exclusively (true) or include all others (null)
					value = event.target.checked === true ? true : null;
				} else {
					value = get(event, ['target', 'checked'], false);
				}
				break;

			case 'date':
				value = value.target.value;
				break;

			default:
				break;
		}

		applyCriteria([{ property, value }]);
	}

	setDateValidation = (property) => (current) => {
		const { filter } = this.props;
		const { startDate, endDate, type } = filter;
		const now = moment();

		if (property === 'startDate' && endDate) {
			return type === constants.JOB_TYPES.upcoming.type
				? current.isBetween(now.subtract(1, 'day'), moment(endDate).add(1, 'day'))
				: current.isSameOrBefore(moment(endDate));
		}

		if (property === 'endDate' && startDate) {
			return type === constants.JOB_TYPES.upcoming.type
				? current.isSameOrAfter(moment(startDate))
				: current.isBetween(moment(startDate).subtract(1, 'day'), now);
		}

		return type === constants.JOB_TYPES.upcoming.type ? current.isSameOrAfter(now.subtract(1, 'day')) : current.isSameOrBefore(now);
	}

	render() {
		const {
			activePage,
			hideDateRange,
			userTypeId,
			locations,
			loading,
			filter : {
				type,
				locationId,
				dateRange,
				includeInactive,
				unStaffed,
				deleted,
				startDate,
				endDate,
				prePay,
			},
		} = this.props;

		const isTypeActive = type === constants.JOB_TYPES.active.type;
		const isTypeUpcoming = type === constants.JOB_TYPES.upcoming.type;
		const isTypeCompleted = type === constants.JOB_TYPES.completed.type;
		const isAdmin = utils.IsUserType(userTypeId, [constants.USER_TYPE.SUPER_ADMIN]);
		const isClient = utils.IsUserType(userTypeId, [constants.USER_TYPE.CLIENT]);

		const dateRangeOptions2 = [
			{ value : 1, label : `${isTypeCompleted ? 'Last' : 'Next'} 24 hours` },
			{ value : 2, label : `${isTypeCompleted ? 'Last' : 'Next'} 48 hours` },
			{ value : 7, label : `${isTypeCompleted ? 'Last' : 'Next'} 7 days` },
			{ value : 30, label : `${isTypeCompleted ? 'Last' : 'Next'} 30 days` },
			{ value : 90, label : `${isTypeCompleted ? 'Last' : 'Next'} 90 days` },
			{ value : 120, label : `${isTypeCompleted ? 'Last' : 'Next'} 120 days` },
			{ value : 'custom', label : 'Custom Date Range' },
		];

		let colSize = 3;

		if (
			(!isAdmin && !isClient && isTypeActive)
			|| (!isAdmin && isClient && (isTypeUpcoming || isTypeCompleted))
			|| (!isAdmin && !isClient && hideDateRange && (isTypeUpcoming || isTypeCompleted))
		) colSize = 4;

		if (
			(!isAdmin && isClient && isTypeActive)
			|| (!isAdmin && isClient && hideDateRange && (isTypeUpcoming || isTypeCompleted))
		) colSize = 6;

		return (
			<React.Fragment>
				<Row>
					<Col md={colSize}>
						{
							this.generateFilterSelector({
								label    : 'Select Location',
								property : 'locationId',
								value    : locationId || '',
								options  : locations,
								loading  : loading.locations,
							})
						}
					</Col>
				</Row>
				<If condition={!hideDateRange && (isTypeUpcoming || isTypeCompleted)}>
					<Row className="margin-top-10 date-pickers">
						<Col md={3}>
							<FormGroup>
								<ControlLabel>Date Range</ControlLabel>
								<Select
									value={dateRange}
									name="dateRange"
									onChange={this.handleOnFilter('dateRange', 'select')}
									options={dateRangeOptions2}
								/>
							</FormGroup>
						</Col>
						<If condition={dateRange === 'custom'}>
							<Col md={3}>
								<FormGroup>
									<label htmlFor="startDate">Start Date</label>
									<input
										name="startDate"
										id="startDate"
										className="form-control"
										type="date"
										placeholder="mm/dd/yyyy"
										value={startDate}
										onChange={this.handleOnFilter('startDate', 'date')}
									/>
								</FormGroup>
							</Col>
							<Col md={3}>
								<FormGroup>
									<label htmlFor="endDate">End Date</label>
									<input
										name="endDate"
										id="endDate"
										className="form-control"
										type="date"
										placeholder="mm/dd/yyyy"
										value={endDate}
										onChange={this.handleOnFilter('endDate', 'date')}
									/>
								</FormGroup>
							</Col>
						</If>
					</Row>
				</If>
				<If condition={activePage !== 'needApproval'}>
					<Row>
						<Col
							md={1}
							className="margin-top-32"
						>
							<FormGroup>
								<Checkbox
									checked={includeInactive}
									name="includeInactive"
									onChange={this.handleOnFilter('includeInactive', 'checkbox')}
									className="margin-top-0 margin-bottom-0"
								>
									Inactive
								</Checkbox>
							</FormGroup>
						</Col>
						<Col
							md={1}
							className="margin-top-32"
						>
							<FormGroup>
								<Checkbox
									checked={unStaffed}
									name="unStaffed"
									onChange={this.handleOnFilter('unStaffed', 'checkbox')}
									className="margin-top-0 margin-bottom-0"
								>
									Unstaffed
								</Checkbox>
							</FormGroup>
						</Col>
						<Col
							md={1}
							className="margin-top-32"
						>
							<FormGroup>
								<Checkbox
									checked={deleted}
									name="deleted"
									onChange={this.handleOnFilter('deleted', 'checkbox')}
									className="margin-top-0 margin-bottom-0"
								>
									Deleted
								</Checkbox>
							</FormGroup>
						</Col>
						<Col
							md={1}
							className="margin-top-32"
						>
							<FormGroup>
								<Checkbox
									checked={prePay}
									onChange={this.handleOnFilter('prePay', 'checkbox')}
									className="margin-top-0 margin-bottom-0"
								>
									Prepaid
								</Checkbox>
							</FormGroup>
						</Col>
					</Row>
				</If>
			</React.Fragment>
		);
	}
}

const mapStateToProps = ({
	app  : { user },
	jobs : {
		locations,
		loaders,
		filter : {
			type,
			locationId,
			dateRange,
			includeInactive,
			unStaffed,
			deleted,
			startDate,
			endDate,
		},
	},
}) => ({
	locations,
	loading    : loaders,
	userTypeId : get(user, ['UserTypeId'], null),
	filter     : {
		type,
		locationId,
		dateRange,
		includeInactive,
		unStaffed,
		deleted,
		startDate,
		endDate,
	},
});

const mapDispatchToProps = (dispatch) => ({
	applyCriteria : bindActionCreators(thunks, dispatch).applyCriteria,
});

Filters.propTypes = {
	activePage    : PropTypes.string,
	applyCriteria : PropTypes.func.isRequired,
	filter        : PropTypes.object,
	hideDateRange : PropTypes.bool,
	loading       : PropTypes.object,
	locations     : PropTypes.array,
	userTypeId    : PropTypes.oneOfType([
		PropTypes.number,
		PropTypes.string,
	]),
};

Filters.defaultProps = {
	activePage    : '',
	filter        : {},
	hideDateRange : false,
	loading       : {},
	locations     : [],
	userTypeId    : '',
};

export default connect(mapStateToProps, mapDispatchToProps)(Filters);
